import { useRef } from 'react';
/**
 * Edit Invoice Page
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState, PageHeader } from '../components/ui';
import { InvoiceForm } from '../components/invoices/InvoiceForm';
import { useInvoice, useUpdateInvoiceDraft } from '../hooks/useInvoices';
import { useToastHelpers } from '../components/ui/Toast';
import type { CreateInvoiceInput } from '@armard/shared';
import type { InvoiceItemDraft } from '../components/invoices/InvoiceItemForm';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';

export const Route = createFileRoute('/invoices_/$id_/edit')({
  component: EditInvoicePage,
});

function EditInvoicePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: invoice, isLoading, error } = useInvoice(id);
  const updateInvoice = useUpdateInvoiceDraft();
  const initialInvoice = useRef(invoice);
  if (invoice && initialInvoice.current?.id !== invoice.id) initialInvoice.current = invoice;

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !invoice) {
    return (
      <ProtectedRoute>
        <AppShell>
          <EmptyState
            title="Rechnung nicht gefunden"
            description="Die angeforderte Rechnung existiert nicht oder wurde gelöscht."
            action={{
              label: 'Zurück zu Rechnungen',
              onClick: () => navigate({ to: '/invoices' }),
            }}
          />
        </AppShell>
      </ProtectedRoute>
    );
  }

  const handleSubmit = async (invoiceData: CreateInvoiceInput, items: InvoiceItemDraft[]) => {
    try {
      await updateInvoice.mutateAsync({
        id: invoice.id,
        invoice: {
          ...invoiceData,
          notes: invoiceData.notes ?? null,
          internalNotes: invoiceData.internalNotes ?? null,
          customerReference: invoiceData.customerReference ?? null,
          version: initialInvoice.current!.version,
        },
        items,
      });
      markFormSaved();

      toast.success('Rechnung aktualisiert');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Fehler beim Speichern'));
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader
          backTo={`/invoices/${invoice.id}`}
          backLabel={invoice.invoiceNumber}
          title="Rechnung bearbeiten"
        />

        <InvoiceForm
          key={invoice.id}
          invoice={initialInvoice.current!}
          onSubmit={handleSubmit}
          isLoading={updateInvoice.isPending}
          onCancel={() => navigate({ to: '/invoices/$id', params: { id: invoice.id } })}
        />
      </AppShell>
    </ProtectedRoute>
  );
}
